import { IconButton, Stack, Typography } from '@mui/material';
import { CrossIcon, EyeIcon, InformationIcon, Loading, ModalSmall } from '@schooly/style';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Counter } from '../../../components/uikit/Counter/Counter.styled';
import {
  ModalContent,
  ModalPanel,
  ModalTitle,
} from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalFooterWithActions } from '../../../components/uikit-components/Modal/ModalFooterWithActions';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { ModalPeopleExtensionPanel } from '../../../components/uikit-components/Modal/ModalPeopleExtensionPanel';
import { getUserFullName } from '../../../helpers/users';
import { ManageGroupModalMode, useManageRelationGroup } from './context/useManageRelationGroup';
import { ManageCriteriaGroups } from './ManageCriteriaGroups';
import { ManageGroupModalRightSidebar } from './ManageGroupModalRightSidebar';
import { ManageGroupModalTooltip } from './ManageGroupModalTooltip';
import { ManageIndividualStudentGroups } from './ManageIndividualStudentGroups';

export const ManageGroupModal: React.FC = () => {
  const {
    actions,
    currentGroups,
    relatedGroups,
    isFetching,
    mode,
    user,
    schoolYear,
    isRelatedGroupsFetching,
    userType,
    isSaving,
    relatedGroupsParams,
  } = useManageRelationGroup();

  const isStudent = userType === 'student';
  const titleId = isStudent ? 'groups-as-individual' : undefined;

  const { individualGroups, criteriaGroups } = useMemo(() => {
    return {
      individualGroups: currentGroups?.filter(
        (g) =>
          g.memberships.some((m) => !!m.is_individual) ||
          // Groups added from sidebar
          !g.memberships.length,
      ),
      criteriaGroups: currentGroups?.filter((g) => g.memberships.some((m) => !!m.criteria)) || [],
    };
  }, [currentGroups]);

  const filteredRelatedGroups = useMemo(() => {
    return (relatedGroups || []).filter((g) => !individualGroups?.some((gr) => gr.id === g.id));
  }, [relatedGroups, individualGroups]);

  const totalGroupsCount = useMemo(
    () => individualGroups?.length + criteriaGroups?.length || 0,
    [criteriaGroups?.length, individualGroups?.length],
  );

  const filterApplied = useMemo(() => {
    return !!(
      relatedGroupsParams.filters.only_tutor_groups?.length ||
      relatedGroupsParams.filters.subject?.length
    );
  }, [relatedGroupsParams.filters]);

  return (
    <ModalSmall open>
      {isFetching || !user ? (
        <Stack flexDirection="row" height="100%" alignItems="center" justifyContent="center">
          <Loading />
        </Stack>
      ) : (
        <>
          <ModalHeader title={<ModalTitle>{getUserFullName(user)}</ModalTitle>}>
            <IconButton onClick={actions.closeModal}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>
          <ModalPanel
            active={mode === ManageGroupModalMode.Individual}
            withBorderBottom={isStudent}
          >
            <Stack direction="row" alignItems="center">
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="h2">
                  <FormattedMessage id="groups-Group-plural" />
                </Typography>
                <Typography variant="h2" color="text.secondary">
                  {schoolYear?.name ?? ''}
                </Typography>
              </Stack>

              {!!totalGroupsCount && <Counter>{Number(totalGroupsCount)}</Counter>}
            </Stack>
          </ModalPanel>
          <ModalPeopleExtensionPanel
            addActionId="groups-AddGroups"
            editActionId="groups-EditGroups"
            titleId={titleId}
            active={mode === ManageGroupModalMode.Individual}
            count={isStudent ? individualGroups?.length : 0}
            onAddClick={() => actions.setMode(ManageGroupModalMode.Individual)}
            sidebarContent={
              <ManageGroupModalRightSidebar
                filterApplied={filterApplied}
                onAddGroup={actions.addGroup}
                currentYear={schoolYear?.name ?? ''}
                isFetching={isRelatedGroupsFetching}
                relatedGroups={filteredRelatedGroups}
                searchQuery={relatedGroupsParams.search_query || ''}
                onSetFilters={actions.setFilters}
                onResetFilters={actions.resetFilters}
                handleSetFiltersQuery={actions.handleSetQuery}
                filters={relatedGroupsParams.filters}
              />
            }
            variant="h3"
            withoutGap
            withoutPadding={!isStudent}
          >
            <ManageIndividualStudentGroups
              fetching={isFetching}
              onRemoveGroup={actions.removeGroup}
              individualGroups={individualGroups}
              criteriaGroups={criteriaGroups}
            />
          </ModalPeopleExtensionPanel>

          {userType === 'student' && !!criteriaGroups?.length && (
            <ModalPeopleExtensionPanel
              addActionId="groups-ViewGroups"
              editActionId="groups-ViewGroups"
              titleId="based-on-criteria"
              editActionIcon={<EyeIcon />}
              active={mode === ManageGroupModalMode.ByCriteria}
              count={criteriaGroups?.length}
              onAddClick={() => actions.setMode(ManageGroupModalMode.ByCriteria)}
              sidebarContent={null}
              variant="h3"
              headerMode={mode === ManageGroupModalMode.ByCriteria}
              disabled={!criteriaGroups?.length}
              infoNode={
                <ManageGroupModalTooltip
                  text={
                    <Typography color="primary.main">
                      <FormattedMessage id="delete-criteria-group-info" />
                    </Typography>
                  }
                  arrow
                >
                  <IconButton inverse>
                    <InformationIcon />
                  </IconButton>
                </ManageGroupModalTooltip>
              }
            >
              <ModalContent active flat sx={{ pt: 0 }}>
                <ManageCriteriaGroups criteriaGroups={criteriaGroups} />
              </ModalContent>
            </ModalPeopleExtensionPanel>
          )}
          <ModalFooterWithActions
            onSaveClick={() => actions.saveGroups(individualGroups)}
            saving={isSaving}
            disabled={!individualGroups?.length}
          />
        </>
      )}
    </ModalSmall>
  );
};
